import { useRef, useEffect, useState, lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { useOrg } from '@/context/OrgContext'
import LoginPage from '@/pages/LoginPage'
import TodayPage from '@/pages/TodayPage'
import DayPage from '@/pages/DayPage'
import ManagerPage from '@/pages/manager/ManagerPage'
import OrgPickerPage from '@/pages/OrgPickerPage'
import NoOrgsPage from '@/pages/NoOrgsPage'
import { Toaster } from '@/components/ui/sonner'
import { LogoMonogram } from '@/components/icons/LogoMonogram'

// Rutas secundarias — fuera del hot-path del crew (/today) y el manager
// (/manager). Lazy-load + Suspense hace que Vite auto-split también
// subdeps compartidas (supabase, lucide, radix inputs), bajando el
// initial chunk de ~215KB a ~104KB gzipped.
const OnboardingPage = lazy(() => import('@/pages/OnboardingPage'))
const ProfilePage = lazy(() => import('@/pages/ProfilePage'))
const MyAgendaPage = lazy(() => import('@/pages/MyAgendaPage'))
const AdminOrgsPage = lazy(() => import('@/pages/admin/AdminOrgsPage'))

function RoleRedirect() {
  const { activeMembership } = useOrg()
  if (activeMembership?.rol_nivel === 'A') return <Navigate to="/manager" replace />
  return <Navigate to="/today" replace />
}

// Auth + org gate for all non-admin app routes. Order matters:
// 1. Not logged in → /login
// 2. No memberships → NoOrgs (or admin page if platform admin)
// 3. Multiple memberships and none selected → OrgPicker
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, profile, loading: authLoading } = useAuth()
  const { memberships, activeMembership, isPlatformAdmin, loading: orgLoading, adminLoading } = useOrg()

  if (authLoading || (user && orgLoading)) return <FullScreenLoader />
  if (!user) return <Navigate to="/login" replace />
  if (profile && !profile.onboarded) return <Navigate to="/onboarding" replace />

  if (memberships.length === 0) {
    if (adminLoading) return <FullScreenLoader />
    if (isPlatformAdmin) return <Navigate to="/admin/orgs" replace />
    return <NoOrgsPage />
  }

  if (!activeMembership) return <OrgPickerPage />

  return <>{children}</>
}

function ManagerRoute({ children }: { children: React.ReactNode }) {
  const { user, profile, loading: authLoading } = useAuth()
  const { memberships, activeMembership, isPlatformAdmin, loading: orgLoading, adminLoading } = useOrg()

  if (authLoading || (user && orgLoading)) return <FullScreenLoader />
  if (!user) return <Navigate to="/login" replace />
  if (profile && !profile.onboarded) return <Navigate to="/onboarding" replace />
  if (memberships.length === 0) {
    if (adminLoading) return <FullScreenLoader />
    if (isPlatformAdmin) return <Navigate to="/admin/orgs" replace />
    return <NoOrgsPage />
  }
  if (!activeMembership) return <OrgPickerPage />
  if (activeMembership.rol_nivel !== 'A') return <Navigate to="/today" replace />
  return <>{children}</>
}

function PlatformAdminRoute({ children }: { children: React.ReactNode }) {
  const { user, profile, loading: authLoading } = useAuth()
  const { isPlatformAdmin, adminLoading } = useOrg()

  if (authLoading || (user && adminLoading)) return <FullScreenLoader />
  if (!user) return <Navigate to="/login" replace />
  if (profile && !profile.onboarded) return <Navigate to="/onboarding" replace />
  if (!isPlatformAdmin) return <Navigate to="/" replace />
  return <>{children}</>
}

// Onboarding route — requires auth but not org membership, and only
// renders when the profile hasn't finished onboarding.
function OnboardingRoute({ children }: { children: React.ReactNode }) {
  const { user, profile, loading: authLoading } = useAuth()
  if (authLoading) return <FullScreenLoader />
  if (!user) return <Navigate to="/login" replace />
  if (profile && profile.onboarded) return <Navigate to="/" replace />
  return <>{children}</>
}

const SPLASH_MIN_MS = 1200

function InitialSplash({ children }: { children: React.ReactNode }) {
  const { loading: authLoading } = useAuth()
  const { loading: orgLoading } = useOrg()
  const dataReady = !authLoading && !orgLoading
  const [minElapsed, setMinElapsed] = useState(false)
  const [fadeOut, setFadeOut] = useState(false)

  useEffect(() => {
    const t = setTimeout(() => setMinElapsed(true), SPLASH_MIN_MS)
    return () => clearTimeout(t)
  }, [])

  const shouldDismiss = dataReady && minElapsed

  useEffect(() => {
    if (!shouldDismiss) return
    setFadeOut(true)
  }, [shouldDismiss])

  if (fadeOut) {
    return (
      <>
        {children}
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-background animate-out fade-out dur-slow ease-in-soft fill-mode-forwards pointer-events-none"
        >
          <SplashContent />
        </div>
      </>
    )
  }

  if (!shouldDismiss) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background">
        <SplashContent />
      </div>
    )
  }

  return <>{children}</>
}

// Splash screen — Backlot direction. TF monogram centered sobre un halo
// radial violet que respira, 'tourflow' wordmark debajo en Geist 600
// tight tracking. Sin barra de progreso: la idea es un brand moment
// intencional, no un loader genérico. SPLASH_MIN_MS asegura que siempre
// se vea lo suficiente aunque los datos carguen más rápido.
function SplashContent() {
  return (
    <div className="relative w-full h-full flex flex-col items-center justify-center gap-7 text-foreground overflow-hidden">
      {/* Radial halo behind the monogram — primary color, heavily blurred,
          breathes entre 0.45 y 0.75 opacity con un tick de escala. */}
      <div
        className="absolute pointer-events-none"
        style={{
          width: 420,
          height: 420,
          background:
            'radial-gradient(circle, color-mix(in oklab, var(--primary) 22%, transparent) 0%, transparent 62%)',
          filter: 'blur(18px)',
          animation: 'splash-halo-breathe 3200ms ease-in-out infinite',
        }}
        aria-hidden
      />
      <div className="relative animate-in fade-in slide-in-from-bottom-2 dur-slow ease-out-soft">
        <LogoMonogram size={72} />
      </div>
      <div
        className="relative animate-in fade-in slide-in-from-bottom-2 dur-slow ease-out-soft"
        style={{ animationDelay: '120ms', animationFillMode: 'backwards' }}
      >
        <p className="text-[26px] font-semibold tracking-[-0.03em] leading-none">tourflow</p>
      </div>
    </div>
  )
}

function FullScreenLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-6 h-6 border-2 border-primary/40 border-t-primary rounded-full animate-spin" />
    </div>
  )
}

// Route hierarchy used to pick a directional slide when navigating between
// top-level sections. Higher number = deeper. If the new level is deeper
// than the previous one we slide in from the right (forward / push).
// If it's shallower we slide in from the left (back / pop). Same or
// unknown → fade only. Internal tab switches inside /manager and
// day-to-day navigation share the same top-level, so they don't remount
// the shell at all.
//
// `today` and `day` share the same level on purpose: they're the same crew
// surface filtered by date, and day-to-day navigation drives its own
// directional slide via `withViewTransition` (see SwipeableDay + the day
// navigator chevrons). Letting AnimatedRoutes also fire its 4px slide here
// would stack two competing animations on the same swipe.
const ROUTE_LEVELS: Record<string, number> = {
  login: 0,
  onboarding: 0,
  root: 1,
  today: 1,
  day: 1,
  manager: 1,
  admin: 1,
  profile: 2,
  'my-agenda': 2,
}

function AnimatedRoutes() {
  const location = useLocation()
  const topLevel = location.pathname.split('/')[1] || 'root'
  const currentLevel = ROUTE_LEVELS[topLevel] ?? 1

  const prevTopLevel = useRef(topLevel)
  const prevLevel = useRef(currentLevel)

  // Compute direction from the *previous* snapshot before the effect below
  // writes the new one. First mount: same-same → fade.
  const direction: 'forward' | 'back' | 'fade' =
    topLevel === prevTopLevel.current
      ? 'fade'
      : currentLevel > prevLevel.current
      ? 'forward'
      : currentLevel < prevLevel.current
      ? 'back'
      : 'fade'

  useEffect(() => {
    prevTopLevel.current = topLevel
    prevLevel.current = currentLevel
  }, [topLevel, currentLevel])

  const animClass =
    direction === 'forward'
      ? 'animate-in fade-in slide-in-from-right-4 dur-base ease-out-soft'
      : direction === 'back'
      ? 'animate-in fade-in slide-in-from-left-4 dur-base ease-out-soft'
      : 'animate-in fade-in dur-base ease-out-soft'

  return (
    <div key={topLevel} className={animClass}>
      <Suspense fallback={<FullScreenLoader />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <RoleRedirect />
            </ProtectedRoute>
          }
        />
        <Route
          path="/today"
          element={
            <ProtectedRoute>
              <TodayPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/day/:date"
          element={
            <ProtectedRoute>
              <DayPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/manager/*"
          element={
            <ManagerRoute>
              <ManagerPage />
            </ManagerRoute>
          }
        />
        <Route
          path="/onboarding"
          element={
            <OnboardingRoute>
              <OnboardingPage />
            </OnboardingRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-agenda"
          element={
            <ProtectedRoute>
              <MyAgendaPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/orgs"
          element={
            <PlatformAdminRoute>
              <AdminOrgsPage />
            </PlatformAdminRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </div>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <InitialSplash>
        <AnimatedRoutes />
      </InitialSplash>
      <Toaster richColors position="bottom-center" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }} />
    </BrowserRouter>
  )
}
